/**
 * voice-v8 — Contacts-API-Client (Phase E5).
 *
 * Wrappt /api/contacts/*. Verwendet Session-Cookie für User-Resolution
 * (gleiches Pattern wie projects-api).
 */

import type { ContactCategory, ContactRecord } from "../../memory/contacts.js";

export type { ContactCategory };
export type ContactDto = ContactRecord;

interface ContactsListResponse {
  items: ContactDto[];
  total: number;
  /** Pre.886 (#144): Kontakt-Anzahl je Kategorie-Key (familie, arbeit, …). */
  categoryCounts: Record<string, number>;
}

export interface CreateContactInput {
  name: string;
  phone?: string | null;
  email?: string | null;
  company?: string | null;
  website?: string | null;
  openingHours?: string | null;
  category?: ContactCategory;
  notes?: string | null;
  avatarColor?: string | null;
}

interface UpdateContactInput {
  name?: string;
  phone?: string | null;
  email?: string | null;
  company?: string | null;
  website?: string | null;
  openingHours?: string | null;
  category?: ContactCategory;
  notes?: string | null;
  avatarColor?: string | null;
}

export async function listContacts(
  opts: { category?: string; q?: string } = {},
): Promise<ContactsListResponse> {
  const url = new URL("/api/contacts", window.location.origin);
  if (opts.category && opts.category !== "alle") {
    url.searchParams.set("category", opts.category);
  }
  if (opts.q) url.searchParams.set("q", opts.q);
  const res = await fetch(url.toString(), { credentials: "same-origin" });
  if (!res.ok) throw new Error(`list contacts failed: ${res.status}`);
  const data = (await res.json()) as ContactsListResponse;
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: typeof data.total === "number" ? data.total : 0,
    categoryCounts:
      data.categoryCounts && typeof data.categoryCounts === "object"
        ? (data.categoryCounts)
        : {},
  };
}

export async function createContact(
  input: CreateContactInput,
): Promise<ContactDto> {
  const body: Record<string, unknown> = { name: input.name };
  if (input.phone !== undefined) body.phone = input.phone;
  if (input.email !== undefined) body.email = input.email;
  if (input.company !== undefined) body.company = input.company;
  if (input.website !== undefined) body.website = input.website;
  if (input.openingHours !== undefined) body.opening_hours = input.openingHours;
  if (input.category !== undefined) body.category = input.category;
  if (input.notes !== undefined) body.notes = input.notes;
  if (input.avatarColor !== undefined) body.avatar_color = input.avatarColor;
  const res = await fetch("/api/contacts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `create contact failed: ${res.status} ${text.slice(0, 200)}`,
    );
  }
  const data = (await res.json()) as { contact: ContactDto };
  return data.contact;
}

export async function updateContact(
  id: string,
  patch: UpdateContactInput,
): Promise<ContactDto> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.phone !== undefined) body.phone = patch.phone;
  if (patch.email !== undefined) body.email = patch.email;
  if (patch.company !== undefined) body.company = patch.company;
  if (patch.website !== undefined) body.website = patch.website;
  if (patch.openingHours !== undefined) body.opening_hours = patch.openingHours;
  if (patch.category !== undefined) body.category = patch.category;
  if (patch.notes !== undefined) body.notes = patch.notes;
  if (patch.avatarColor !== undefined) body.avatar_color = patch.avatarColor;
  const res = await fetch(`/api/contacts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `update contact failed: ${res.status} ${text.slice(0, 200)}`,
    );
  }
  const data = (await res.json()) as { contact: ContactDto };
  return data.contact;
}

export async function deleteContact(id: string): Promise<void> {
  const res = await fetch(`/api/contacts/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`delete contact failed: ${res.status}`);
}
